import { Injectable } from '@angular/core';
import * as THREE from 'three';

import { ProfileSelection } from './profile-window.component';

export interface ProfilePointData {
  numPoints: number;
  data: {
    position: Float32Array | number[];
    color?: Uint8Array | number[];
    mileage: Float32Array | number[];
    [key: string]: ArrayLike<number> | undefined;
  };
  projectedBox?: THREE.Box3;
}

export interface ProfileBatch {
  geometry: THREE.BufferGeometry;
  material: any;
  sceneNode: THREE.Points;
  geometryNode: {
    estimatedSpacing: number;
    geometry: THREE.BufferGeometry;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ProfileWindowService {
  private component: any;

  setComponent(component: any) {
    this.component = component;
  }

  copyMaterial(source: any, target: any) {
    if (!source?.uniforms || !target?.uniforms) return;

    for (const name of Object.keys(target.uniforms)) {
      if (source.uniforms[name]) {
        target.uniforms[name].value = source.uniforms[name].value;
      }
    }

    target.gradientTexture = source.gradientTexture;
    target.visibleNodesTexture = source.visibleNodesTexture;
    target.classificationTexture = source.classificationTexture;
    target.activeAttributeName = source.activeAttributeName;
    target.ranges = source.ranges;
  }

  createBatch(geometry: THREE.BufferGeometry, material: any): ProfileBatch {
    const sceneNode = new THREE.Points(geometry, material);

    return {
      geometry,
      material,
      sceneNode,
      geometryNode: {
        estimatedSpacing: 1.0,
        geometry,
      },
    };
  }

  selectPoint(
    mileage: number,
    elevation: number,
    radius: number,
    pointclouds: Map<any, any>,
  ): ProfileSelection | null {
    interface ClosestPoint {
      distance: number;
      pointcloud: any | null;
      points: ProfilePointData | null;
      index: number | null;
    }

    const closest: ClosestPoint = {
      distance: Infinity,
      pointcloud: null,
      points: null,
      index: null,
    };

    const pointBox = new THREE.Box2(
      new THREE.Vector2(mileage - radius, elevation - radius),
      new THREE.Vector2(mileage + radius, elevation + radius),
    );

    for (const [pointcloud, entry] of pointclouds) {
      for (const points of entry.points) {
        if (!points.projectedBox) continue;

        const projectedBox = points.projectedBox;
        const box2D = new THREE.Box2(
          new THREE.Vector2(projectedBox.min.x, projectedBox.min.z),
          new THREE.Vector2(projectedBox.max.x, projectedBox.max.z),
        );

        if (!pointBox.intersectsBox(box2D)) {
          continue;
        }

        // Check individual points
        for (let i = 0; i < points.numPoints; i++) {
          const x = points.data.mileage[i] || 0;
          const z = (points.data.position[3 * i + 2] || 0) + (pointcloud.position?.z || 0);

          if (pointBox.containsPoint(new THREE.Vector2(x, z))) {
            const distance = Math.sqrt((x - mileage) ** 2 + (z - elevation) ** 2);
            if (distance < closest.distance) {
              closest.distance = distance;
              closest.pointcloud = pointcloud;
              closest.points = points;
              closest.index = i;
            }
          }
        }
      }
    }

    if (
      closest.distance < Infinity &&
      closest.points &&
      closest.index !== null &&
      closest.pointcloud
    ) {
      const i = closest.index;
      const points = closest.points;

      const position = new THREE.Vector3(
        (points.data.position[3 * i + 0] || 0) + (closest.pointcloud.position?.x || 0),
        (points.data.position[3 * i + 1] || 0) + (closest.pointcloud.position?.y || 0),
        (points.data.position[3 * i + 2] || 0) + (closest.pointcloud.position?.z || 0),
      );

      let rgb: [number, number, number] = [255, 255, 255];
      if (points.data.color) {
        rgb = [
          points.data.color[4 * i + 0] || 255,
          points.data.color[4 * i + 1] || 255,
          points.data.color[4 * i + 2] || 255,
        ];
      }

      return {
        position,
        rgb,
        elevation: position.z,
        mileage: points.data.mileage[i] || 0,
      };
    }

    return null;
  }

  exportToCSV(pointclouds: Map<any, any>): string {
    const lines: string[] = ['X,Y,Z,R,G,B'];

    for (const [pointcloud, entry] of pointclouds) {
      for (const points of entry.points) {
        for (let i = 0; i < points.numPoints; i++) {
          const x = (points.data.position[3 * i + 0] || 0) + (pointcloud.position?.x || 0);
          const y = (points.data.position[3 * i + 1] || 0) + (pointcloud.position?.y || 0);
          const z = (points.data.position[3 * i + 2] || 0) + (pointcloud.position?.z || 0);

          let r = 255,
            g = 255,
            b = 255;
          if (points.data.color) {
            r = points.data.color[4 * i + 0] || 255;
            g = points.data.color[4 * i + 1] || 255;
            b = points.data.color[4 * i + 2] || 255;
          }

          lines.push(`${x},${y},${z},${r},${g},${b}`);
        }
      }
    }

    return lines.join('\n');
  }

  exportToDXF2D(pointclouds: Map<any, any>): string {
    // Simplified DXF 2D export
    let dxf = '0\nSECTION\n2\nENTITIES\n';

    for (const [pointcloud, entry] of pointclouds) {
      for (const points of entry.points) {
        for (let i = 0; i < points.numPoints; i++) {
          const x = points.data.mileage[i] || 0;
          const z = (points.data.position[3 * i + 2] || 0) + (pointcloud.position?.z || 0);

          dxf += `0\nPOINT\n8\nProfile\n10\n${x}\n20\n${z}\n30\n0.0\n`;
        }
      }
    }

    dxf += '0\nENDSEC\n0\nEOF\n';
    return dxf;
  }

  exportToDXF3D(pointclouds: Map<any, any>): string {
    // Simplified DXF 3D export
    let dxf = '0\nSECTION\n2\nENTITIES\n';

    for (const [pointcloud, entry] of pointclouds) {
      for (const points of entry.points) {
        for (let i = 0; i < points.numPoints; i++) {
          const x = (points.data.position[3 * i + 0] || 0) + (pointcloud.position?.x || 0);
          const y = (points.data.position[3 * i + 1] || 0) + (pointcloud.position?.y || 0);
          const z = (points.data.position[3 * i + 2] || 0) + (pointcloud.position?.z || 0);

          dxf += `0\nPOINT\n8\nProfile\n10\n${x}\n20\n${y}\n30\n${z}\n`;
        }
      }
    }

    dxf += '0\nENDSEC\n0\nEOF\n';
    return dxf;
  }

  exportToLAS(pointclouds: Map<any, any>): ArrayBuffer {
    // Collect all points first to calculate bounding box
    const allPoints: { x: number; y: number; z: number; r: number; g: number; b: number }[] =
      [];
    const boundingBox = new THREE.Box3();

    for (const [pointcloud, entry] of pointclouds) {
      for (const points of entry.points) {
        for (let i = 0; i < points.numPoints; i++) {
          const x = (points.data.position[3 * i + 0] || 0) + (pointcloud.position?.x || 0);
          const y = (points.data.position[3 * i + 1] || 0) + (pointcloud.position?.y || 0);
          const z = (points.data.position[3 * i + 2] || 0) + (pointcloud.position?.z || 0);

          let r = 255,
            g = 255,
            b = 255;
          if (points.data.color) {
            r = points.data.color[4 * i + 0] || 255;
            g = points.data.color[4 * i + 1] || 255;
            b = points.data.color[4 * i + 2] || 255;
          }

          allPoints.push({ x, y, z, r, g, b });
          boundingBox.expandByPoint(new THREE.Vector3(x, y, z));
        }
      }
    }

    if (allPoints.length === 0) {
      // Return empty LAS file if no points
      return new ArrayBuffer(227);
    }

    const offset = boundingBox.min.clone();
    const diagonal = boundingBox.min.distanceTo(boundingBox.max);
    const scale =
      diagonal > 1000 * 1000
        ? new THREE.Vector3(0.01, 0.01, 0.01)
        : new THREE.Vector3(0.001, 0.001, 0.001);

    const buffer = new ArrayBuffer(227 + 28 * allPoints.length);
    const view = new DataView(buffer);
    const u8View = new Uint8Array(buffer);

    // Helper to write string
    const setString = (str: string, offset: number) => {
      for (let i = 0; i < str.length; i++) {
        u8View[offset + i] = str.charCodeAt(i);
      }
    };

    // LAS Header
    setString('LASF', 0);
    u8View[24] = 1; // Version major
    u8View[25] = 2; // Version minor
    setString('Insights Digital Twin', 58); // Generating software
    view.setUint16(94, 227, true); // Header size
    view.setUint32(96, 227, true); // Offset to point data
    u8View[104] = 2; // Point data format (RGB)
    view.setUint16(105, 28, true); // Point record length
    view.setUint32(107, allPoints.length, true); // Number of points

    // Scale factors
    view.setFloat64(131, scale.x, true);
    view.setFloat64(139, scale.y, true);
    view.setFloat64(147, scale.z, true);

    // Offsets
    view.setFloat64(155, offset.x, true);
    view.setFloat64(163, offset.y, true);
    view.setFloat64(171, offset.z, true);

    // Bounding box
    view.setFloat64(179, boundingBox.max.x, true);
    view.setFloat64(187, boundingBox.min.x, true);
    view.setFloat64(195, boundingBox.max.y, true);
    view.setFloat64(203, boundingBox.min.y, true);
    view.setFloat64(211, boundingBox.max.z, true);
    view.setFloat64(219, boundingBox.min.z, true);

    // Write point data
    let boffset = 227;
    for (const point of allPoints) {
      const ux = Math.floor((point.x - offset.x) / scale.x);
      const uy = Math.floor((point.y - offset.y) / scale.y);
      const uz = Math.floor((point.z - offset.z) / scale.z);

      view.setInt32(boffset + 0, ux, true);
      view.setInt32(boffset + 4, uy, true);
      view.setInt32(boffset + 8, uz, true);
      view.setUint16(boffset + 12, 0, true); // Intensity
      u8View[boffset + 14] = 0; // Return number
      u8View[boffset + 15] = 0; // Number of returns
      u8View[boffset + 16] = 0; // Classification
      view.setInt8(boffset + 17, 0); // Scan angle
      u8View[boffset + 18] = 0; // User data
      view.setUint16(boffset + 19, 0, true); // Point source ID
      view.setUint16(boffset + 20, point.r * 256, true); // Red
      view.setUint16(boffset + 22, point.g * 256, true); // Green
      view.setUint16(boffset + 24, point.b * 256, true); // Blue

      boffset += 28;
    }

    return buffer;
  }
}
